import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyGridContainer } from 'routes/HumanResourcesModule/components/ui/StyGridContainer';
import { getDefaultGridProps } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { DetailedAgentArrivalHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/types';
import UpdateAgentArrivalHypothesisCellRenderer from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/ArrivalCellRenderer/UpdateAgentArrivalHypothesisCellRenderer';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { getColDefs } from './helpers/colDefinitions';
import NoDataResume from '../NoDataResume';
import { GetRowIdParams } from 'ag-grid-community';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';

type Props = {
    data: DetailedAgentArrivalHypothesis[];
    hrSimulation: HrSimulation;
    recurrence: RecurrenceEnum;
};

const ArrivalHypothesesGrid = ({ data, hrSimulation, recurrence }: Props) => {
    const { t } = useTranslation();

    const getRowId = useCallback(
        (params: GetRowIdParams) => Object.values(params.data).join('-'),
        [],
    );
    const filteredData = data.filter((o) => o.recurrence === recurrence);
    const colDefs = useMemo(() => getColDefs(hrSimulation), [hrSimulation.id]);
    const defaultProps = getDefaultGridProps();
    const gridProps = {
        ...defaultProps,
        getRowId,
        defaultColDef: {
            flex: 1,
        },
        components: {
            ...defaultProps.components,
            updateAgentArrivalHypothesisCellRenderer:
                UpdateAgentArrivalHypothesisCellRenderer,
        },
    };

    return (
        <>
            {filteredData.length === 0 ? (
                <NoDataResume
                    text={t('humanResources.simulation.noArrivalHypotheses')}
                />
            ) : (
                <StyGridContainer>
                    <AgGridReact
                        {...gridProps}
                        rowData={filteredData}
                        columnDefs={colDefs}
                    />
                </StyGridContainer>
            )}
        </>
    );
};

export default ArrivalHypothesesGrid;
